import { useEffect, useRef, useState } from "react";
import { Task, fetchTasks, fetchTasksNumber, isTaskOverdue } from "services/tasks";
import {
  Notification,
  getNotifications,
  isHighSeverityNotification,
} from "services/notifications";
import { ServerErrorResponse } from "services/errors";
import { logError } from "services/logging";
import bellImg from "images/bell.svg";
import notchImg from "images/notch.svg";
import { useTranslation } from "react-i18next";
import { activateOnKeys } from "utils/a11y";
import NotificationsList from "./components/NotificationsList/NotificationsList";
import "./UserNotifications.css";

const UPDATE_PERIOD = 5 * 60 * 1000; // 5 minutes

const isHighSeverityTaskOrNotification = (
  tasks: Task[],
  notifications: Notification[]
) => {
  return (
    tasks.some(task => isTaskOverdue(task)) ||
    notifications.some(notification => isHighSeverityNotification(notification))
  );
};

interface UserNotificationsState {
  tasks: Task[];
  notifications: Notification[];
  numberOfTasks: number;
  numberOfNotifications: number;
  isHighSeverity: boolean;
  showDetails: boolean;
}

export default function UserNotifications() {
  const [state, setState] = useState<UserNotificationsState>({
    tasks: [],
    notifications: [],
    numberOfTasks: 0,
    numberOfNotifications: 0,
    isHighSeverity: false,
    showDetails: false,
  });
  const [lastFetched, setLastFetched] = useState(new Date().getTime());
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener("mousedown", handleClick, false);
    refresh();
    const updateInterval = setInterval(async () => {
      if (new Date().getTime() - lastFetched > UPDATE_PERIOD) {
        updateTasksNumber();
        await updateNotifications();
      }
    }, UPDATE_PERIOD);
    return () => {
      document.removeEventListener("mousedown", handleClick, false);
      updateInterval && clearInterval(updateInterval);
    };
    // eslint-disable-next-line
  }, []);

  const refresh = async (callback?: any) => {
    await Promise.all([
      new Promise(resolve =>
        fetchTasks(
          undefined,
          (resp: any) => {
            setState(prev => ({
              ...prev,
              numberOfTasks: resp.list.length,
              tasks: resp.list,
              isHighSeverity: isHighSeverityTaskOrNotification(
                resp.list,
                prev.notifications
              ),
            }));
            callback && callback();
            resolve(undefined);
          },
          (errors: ServerErrorResponse) => {
            // We don't notify user about these errors.
            logError("got error while retrieving tasks", {
              from: "UserNotifications#refresh",
              errors: errors,
            });
          }
        )
      ),
      updateNotifications(),
    ]);
    setLastFetched(new Date().getTime());
  };

  const updateTasksNumber = () => {
    fetchTasksNumber(
      undefined,
      (count: number) => {
        setState(prev => ({ ...prev, numberOfTasks: count }));
      },
      () => {
        // We don't notify user about these errors.
        // Ignore since it's fetched in interval and we can get a lot of this errors if user's
        // browser is just sleeping.
      }
    );
  };

  const updateNotifications = () => {
    return new Promise(resolve =>
      getNotifications(
        undefined,
        ({ list }) => {
          setState(prev => ({
            ...prev,
            notifications: list,
            numberOfNotifications: list.length || 0,
            isHighSeverity: isHighSeverityTaskOrNotification(prev.tasks, list),
          }));
          resolve(undefined);
        },
        () => {}
      )
    );
  };

  const handleClick = (evt: any) => {
    if (!container || !container.current?.contains(evt.target)) {
      // Click outside, close notifications.
      closeDetails();
    }
  };

  const handleIconClick = () => {
    if (!state.showDetails) {
      // If we open details
      refresh(() => {
        setState(prev => ({ ...prev, showDetails: true }));
      });
    } else {
      setState(prev => ({ ...prev, showDetails: false }));
    }
  };

  const closeDetails = () => {
    setState(prev => ({ ...prev, showDetails: false }));
  };

  return (
    <div className="user-notifications" ref={container}>
      <div className={"icon-container" + (state.isHighSeverity ? " has-overdue" : "")}>
        <div
          onClick={handleIconClick}
          role="button"
          tabIndex={0}
          aria-haspopup="true"
          aria-expanded={state.showDetails}
          aria-label="Notifications"
          onKeyDown={activateOnKeys(handleIconClick)}
        >
          <div className={"badge-container"}>
            {state.showDetails && (
              <NotificationsContainer
                tasks={state.tasks}
                notifications={state.notifications}
                closeDetails={closeDetails}
                refresh={refresh}
              />
            )}
            <img src={bellImg} alt="bell" />
            {state.numberOfTasks + state.numberOfNotifications > 0 && (
              <span className="badge">
                {state.numberOfTasks + state.numberOfNotifications}
              </span>
            )}
            {state.isHighSeverity && <span className="exclaimer">!</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

interface NotificationsContainerProps {
  tasks: Task[];
  notifications: Notification[];
  closeDetails: () => void;
  refresh: () => void;
}

function NotificationsContainer({
  tasks,
  notifications,
  closeDetails,
  refresh,
}: NotificationsContainerProps) {
  const { t } = useTranslation();

  return (
    <div className="notifications-container">
      <img src={notchImg} alt="" className="notch" />
      <div className="notifications-header">
        <img src={bellImg} alt="bell" />
        {t("Common.pageHeader.notificationsTitle")}
      </div>
      <NotificationsList
        tasks={tasks}
        notifications={notifications}
        closeDetails={closeDetails}
        refresh={refresh}
      />
    </div>
  );
}
